import { Dialog } from '@mui/material';
import React, { PropsWithChildren, useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { styled } from 'styled-components';

import { BRAND_NAME, HOSPITAL_NAME } from '../../../../shared/env';
import { useUnregister } from '../../../../shared/hooks/accountHook';
import healthhubLogo from '../../assets/icons/icon_healthhub-dark.png';
import {
  BlueRoundedButton,
  BlueRoundedButtonWithIcon,
  ButtonContainer,
} from '../../common/components/BlueRoundedButton';
import { ErrorMessage } from '../../common/components/EditModal/styledComponents';
import {
  CloseButtonContainer,
  ModalCloseButton,
} from '../../common/components/ModalCloseButton';
import { TextFieldSlot } from '../../common/components/Slot';
import { MobileFullTextField } from '../../common/components/Slot/TextFieldSlot';
import { WindowSizeContext, WindowSize } from '../../hooks/windowSizeHook';
import { MobileModalHeader } from '../Layout/MobileModalHeader';

const Container = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 575px;
  height: 550px;
  padding: 53px 85px 36px 85px;
  box-sizing: border-box;
`;

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  padding: 44px 28px;
`;

const HealthhubLogo = styled.img`
  align-self: center;
  width: 206px;
  height: fit-content;
`;

const HospitalLogo = styled.div`
  font-size: 24px;
  line-height: 24px;
  font-weight: 700;
  text-align: center;
  color: #233251;
`;

const ModalTitle = styled.span`
  margin-top: 27px;
  font-size: 18px;
  line-height: 35px;
  font-weight: 500;
  text-align: center;
  color: var(--HScan_dark_blue, #16172c);
`;

const TextContainer = styled.div`
  margin-top: 39px;
  margin-bottom: 34px;
  text-align: center;
  font-size: 13px;
  line-height: 31px;
  font-weight: 400;
  color: var(--HScan_left_menu_grey, #666973);
  word-break: keep-all;
`;

const HighlightText = styled.span`
  color: var(--HScan_Light_Blue, #2e5be0);
`;

const ConfirmModalContainer = styled(Container)`
  justify-content: center;
  align-items: center;
  max-width: 530px;
  max-height: 300px;
`;

const ConfirmModalText = styled(ModalTitle)`
  margin: 0;
  word-break: keep-all;
`;

const ModalContent = (props: PropsWithChildren) => {
  const { children } = props;
  const isMobile = useContext(WindowSizeContext) === WindowSize.MOBILE;
  return isMobile ? (
    <MobileContainer>{children}</MobileContainer>
  ) : (
    <Container>{children}</Container>
  );
};
const ConfirmModalContent = (props: PropsWithChildren) => {
  const { children } = props;
  const isMobile = useContext(WindowSizeContext) === WindowSize.MOBILE;
  return isMobile ? (
    <MobileContainer>{children}</MobileContainer>
  ) : (
    <ConfirmModalContainer>{children}</ConfirmModalContainer>
  );
};
interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
}
export const DeleteAccountModal = (props: DeleteAccountModalProps) => {
  const { open, onClose } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.agreement.modal',
  });
  const { t: globalT } = useTranslation('translation');

  const { mutate: unregister } = useUnregister({
    onSuccess: () => {
      onCloseHandler();
      setOpenConfirmModal(true);
    },
    onError: () => setShowErrorMessage(true),
  });
  const [password, setPassword] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const navigate = useNavigate();
  const onUnregisterAccountHandler = () => {
    if (password?.length) {
      unregister(password);
    } else {
      setShowErrorMessage(true);
    }
  };

  const onCloseHandler = () => {
    onClose();
    setPassword('');
    setShowErrorMessage(false);
  };

  const isMobile = useContext(WindowSizeContext) === WindowSize.MOBILE;

  return (
    <>
      <Dialog onClose={onCloseHandler} open={open} fullScreen={isMobile}>
        {isMobile && <MobileModalHeader onGoBack={onClose} />}
        <form
          onSubmit={e => {
            e.preventDefault();
            onUnregisterAccountHandler();
          }}>
          <ModalContent>
            <CloseButtonContainer>
              <ModalCloseButton onClick={onClose} />
            </CloseButtonContainer>
            {HOSPITAL_NAME ? (
              <HospitalLogo>{HOSPITAL_NAME}</HospitalLogo>
            ) : (
              <HealthhubLogo src={healthhubLogo} />
            )}
            <ModalTitle>{t('title')}</ModalTitle>
            <TextContainer>
              {t('info.title')} <br />
              {t('info.list.0')} <br />
              <Trans
                t={t}
                i18nKey="info.list.1"
                components={[<HighlightText />]}
              />
              <br />
              {t('info.list.2', {
                hospitalName: HOSPITAL_NAME || globalT('common.healthhub'),
              })}
              <br />
            </TextContainer>
            {isMobile ? (
              <MobileFullTextField
                type="password"
                placeholder={`${t('password')}`}
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            ) : (
              <TextFieldSlot
                type="password"
                label={`${t('password')}:`}
                textAlign="center"
                placeholder={`${t('password')}`}
                value={password}
                onChange={value => setPassword(value)}
                fullwidth
              />
            )}
            <ErrorMessage>
              {showErrorMessage ? t('errorMessage') : ''}
            </ErrorMessage>
            <ButtonContainer>
              <BlueRoundedButton marginTop="33px">
                {t('button')}
              </BlueRoundedButton>
            </ButtonContainer>
          </ModalContent>
        </form>
      </Dialog>
      <Dialog open={openConfirmModal} fullScreen={isMobile}>
        <ConfirmModalContent>
          <ConfirmModalText>
            <Trans
              t={t}
              i18nKey="confirmText"
              values={{ brandName: BRAND_NAME }}
              components={[<br />]}
            />
          </ConfirmModalText>
          <BlueRoundedButtonWithIcon
            marginTop="33px"
            onClick={() => {
              navigate('/');
              setOpenConfirmModal(false);
            }}>
            {t('confirmButton')}
          </BlueRoundedButtonWithIcon>
        </ConfirmModalContent>
      </Dialog>
    </>
  );
};
